import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";
import { api, ApiError } from "../lib/api";

type InterviewItem = {
  id: string;
  title: string;
  status: "in_progress" | "completed" | "abandoned";
  stage: string;
  question_count: number;
  created_at: string;
};

const STATUS_LABEL: Record<InterviewItem["status"], string> = {
  in_progress: "In Progress",
  completed: "Completed",
  abandoned: "Abandoned",
};

export default function AIInterview() {
  const { credits, subscription, refreshSubscription } = useAuth();
  const navigate = useNavigate();

  const isPaid =
    subscription &&
    (subscription.plan === "pro_talent" || subscription.plan === "team_fit" || subscription.plan === "scale_up") &&
    (subscription.status === "active" || subscription.status === "trialing");
  const isOutOfCredits = !isPaid && (credits?.interview_credits ?? 0) <= 0;

  const [items, setItems] = useState<InterviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ items: InterviewItem[] }>("/api/interview")
      .then((res) => setItems(res.items))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load interview list."))
      .finally(() => setLoading(false));
  }, []);

  async function startInterview() {
    if (isOutOfCredits) {
      setError("You have 0 interview credits remaining. Please upgrade your plan.");
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const res = await api.post<{ interviewId: string }>("/api/interview/start", { title: "AI Chat Interview" });
      await refreshSubscription();
      navigate(`/app/interview/${res.interviewId}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === "OUT_OF_CREDITS") {
        setError("You have 0 interview credits remaining. Please upgrade your plan or buy credits to start.");
        await refreshSubscription();
      } else {
        setError(err instanceof Error ? err.message : "Failed to start interview.");
      }
    } finally {
      setStarting(false);
    }
  }

  return (
    <AppLayout title="AI Interview">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-navy">AI Interview</h1>
          <p className="text-xs text-slate-500">Practice interactive AI interviews and receive detailed competency reports.</p>
        </div>
        {!isPaid && (
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                isOutOfCredits ? "bg-rose-100 text-rose-700 border-rose-300 animate-pulse" : "bg-slate-50 text-slate-700 border-slate-200"
              }`}
            >
              💳 Interview Credits: {credits?.interview_credits ?? 0}
            </span>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1 rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white transition hover:bg-navy-light"
            >
              ⚡ Upgrade Plan
            </Link>
          </div>
        )}
      </div>

      {isOutOfCredits && (
        <Card className="mb-4 border border-rose-200 bg-rose-50 !p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-500 text-white font-bold text-sm">⚡</span>
              <div>
                <p className="text-xs font-bold text-rose-900 sm:text-sm">0 Interview Credits Remaining</p>
                <p className="text-[11px] text-rose-700">You need interview credits to start a new AI Chat Interview. Upgrade your plan to continue.</p>
              </div>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1 rounded-lg bg-teal px-3.5 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-teal-dark"
            >
              ⚡ Upgrade / Buy Credits →
            </Link>
          </div>
        </Card>
      )}

      <Card className="border border-slate-100 bg-gradient-to-br from-navy to-navy-light text-white">
        <p className="text-base font-bold">AI Chat Interview</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-200">
          Answer questions naturally. Our AI interviewer asks follow-up questions and evaluates your responses in real time.
        </p>
        <Button variant="secondary" size="sm" className="mt-3" loading={starting} disabled={isOutOfCredits} onClick={startInterview}>
          Start New Interview
        </Button>
      </Card>

      {error && <Card className="mt-3 border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>}
      {loading && <p className="mt-3 text-xs text-slate-400">Loading interviews...</p>}

      <div className="mt-4 space-y-2">
        {items.map((iv) => (
          <button key={iv.id} className="w-full text-left" onClick={() => navigate(iv.status === "completed" ? `/app/interview/${iv.id}/report` : `/app/interview/${iv.id}`)}>
            <Card className="border border-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-navy">{iv.title}</p>
                  <p className="text-[11px] text-slate-400">
                    {iv.question_count} questions · {new Date(iv.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </p>
                </div>
                <span
                  className={`rounded-full px-2 py-1 text-[10px] font-medium ${
                    iv.status === "completed" ? "bg-teal/10 text-teal-dark" : "bg-slate-50 text-slate-500"
                  }`}
                >
                  {STATUS_LABEL[iv.status]}
                </span>
              </div>
            </Card>
          </button>
        ))}
      </div>
    </AppLayout>
  );
}
